import {
  getCountryCodeFromMetaTag,
  mscGet,
  mscRemove,
  mscSet,
  saltedHash,
  setTriggerSource,
  trackEvent,
  triggerInfo,
} from '@msc-b2c/shared-utils';

const isIE = getCountryCodeFromMetaTag().toLowerCase().includes('ie');
let hasInteracted = false;
let hasSucceded = false;
let hasFailed = false;
let failCheck = false;
// Track form impressions when the form is first displayed
const trackFormImpression = (
  formTrigger: string,
  leadName: string,
  leadType: string,
  leadStepNumber: string
) => {
  trackEvent('lead_form_impression', {
    form_trigger: formTrigger,
    lead_name: leadName,
    lead_type: leadType,
    lead_step_name: 'view form',
    lead_step_number: leadStepNumber,
  });
};

// Track form interactions when the user starts interacting with the form
const trackFormInteraction = (
  formTrigger: string,
  leadName: string,
  leadType: string,
  leadStepNumber: string
) => {
  trackEvent('lead_form_start', {
    form_trigger: formTrigger,
    lead_name: leadName,
    lead_type: leadType,
    lead_step_name: 'form interaction',
    lead_step_number: leadStepNumber,
  });
};

// Track form success when the form is successfully submitted
export const trackFormSuccess = (
  formTrigger: string,
  leadName: string,
  leadType: string,
  leadStepNumber: string,
  leadId: string,
  leadEmail: string,
  marketing_consent: string,
  profiling_consent?: string
) => {
  // Construct event data
  const eventData: Record<string, string> = {
    form_trigger: formTrigger,
    lead_name: leadName,
    lead_type: leadType,
    lead_step_name: 'form success',
    lead_step_number: leadStepNumber,
    lead_id: leadId,
    lead_email: leadEmail,
  };

  // Conditionally add `marketing_consent`
  eventData['marketing_consent'] = marketing_consent;
  if (profiling_consent) {
    eventData['profiling_consent'] = profiling_consent;
  }

  trackEvent('lead_form_success', eventData);
};

// Track form errors when the form submission fails
export const trackFormError = (
  formTrigger: string,
  leadName: string,
  leadType: string,
  leadStepNumber: string,
  leadErrorType: string,
  leadErrorMessage: string
) => {
  trackEvent('lead_form_error', {
    form_trigger: formTrigger,
    lead_name: leadName,
    lead_type: leadType,
    lead_step_name: 'form error',
    lead_step_number: leadStepNumber,
    lead_error_type: leadErrorType,
    lead_error_message: leadErrorMessage,
  });
};

// Helper map for lead names based on form code
const leadNameMap: Record<string, string> = {
  cmbDem: 'call me back-interested',
  cmbStickyBanner: 'sticky banner',
  cmbSocialMedia: 'social media',
  cmbPromoForm: 'promo',
  cmbNurturingDem: 'nurturing-dem',
  cmb: isIE ? 'call me back - already booked' : 'call me back - interested',
  cmbLeadsMoving: 'leads moving',
  contactUs: isIE ? 'contact us' : 'call me back - already booked',
  contactUS: isIE ? 'contact us' : 'call me back - already booked',
  contactUs_DE: isIE ? 'contact us' : 'call me back - already booked',
  contactUs_FR: isIE ? 'contact us' : 'call me back - already booked',
  cmbR4Q: 'request for quotation',
  callMeBack: 'status match program',
  statusMatchIRL: 'status match program',
  newsletterSignup: 'newsletter-subscription',
  'guest(s) container': 'guest info',
  'freeze the price': 'freeze the price',
  WC_StatusMatch: 'status match program',
  statusMatchUK: 'status match program',
  statusMatch: 'status match program',
  'form-StatusmatchID': 'status match program',
  statusMatch_DE: 'status match program',
  statusMatch_FR: 'status match program',
  cmbCartrecovery_popup: 'call me back - cart recovery',
};

// Set up form tracking for impressions and interactions
const setupFormTracking = (formNode: HTMLElement) => {
  let formStarted = false; // Flag to ensure 'lead_form_start' is fired once
  let formImpressed = false; // Flag to ensure 'lead_form_impression' is fired once
  const formName = formNode.querySelector<HTMLInputElement>('input[name="webformCode"]');
  const webformCode = formName ? formName.value : '';
  let altWebformCode = webformCode;
  if (webformCode === 'customValues') {
    // is reading value about meta customWebformCode_skip
    altWebformCode =
      formName?.nextElementSibling instanceof HTMLInputElement
        ? formName.nextElementSibling.value
        : '';
  }

  let formTrigger = triggerInfo.source;
  const formId = formName?.closest('form')?.id;
  if (formId) mscSet('session', formId, formTrigger);
  let leadName = leadNameMap[webformCode] ?? '';
  if (webformCode === 'customValues') leadName = altWebformCode ? leadNameMap[altWebformCode] : '';

  if (!isIE) {
    if (altWebformCode == 'cmbStickyBanner') {
      leadName = 'call me back - interested';
      if (
        mscGet<string>('session', 'triggerSource') != 'header menu' &&
        mscGet<string>('session', 'triggerSource') != 'footer'
      )
        formTrigger = 'sticky banner';
      else if (mscGet<string>('session', 'triggerSource') == 'header menu') {
        formTrigger = 'header menu';
        leadName = 'call me back - interested';
      }
    }

    if (altWebformCode == 'cmbR4Q' && mscGet<string>('session', 'virtualPageName') != 'search') {
      formTrigger = 'internal link';
    }
  }

  // Determine the lead type (form page or pop up)
  let leadType = '';
  if (!isIE) {
    if (altWebformCode == 'cmbStickyBanner') {
      if (
        mscGet<string>('session', 'triggerSource') != 'header menu' &&
        mscGet<string>('session', 'triggerSource') != 'footer'
      )
        leadType = 'modal';
      else if (mscGet<string>('session', 'triggerSource') == 'header menu') leadType = 'popup';
      else {
        leadType =
          formNode.closest('.modal') || formNode.closest('.cmp-container__modal')
            ? 'pop up'
            : 'form page';
      }
    } else {
      leadType =
        formNode.closest('.modal') || formNode.closest('.cmp-container__modal')
          ? 'pop up'
          : 'form page';
    }
  } else {
    leadType =
      formNode.closest('.modal') || formNode.closest('.cmp-container__modal')
        ? 'pop up'
        : 'form page';
  }
  const leadStepNumber = 'single-step'; // Currently, all forms are single-step

  let pageLoaded = true;
  // Track form impression if not already tracked
  if (!formImpressed) {
    if (leadType == 'form page' && !pageLoaded) {
      if (formId) {
        if (formId != mscGet<string>('session', 'formId')) {
          formImpressed = true;
          trackFormImpression(formTrigger, leadName, leadType, leadStepNumber);
        }
        mscSet('session', 'formId', formId);
      }
    } else {
      formImpressed = true;
      trackFormImpression(formTrigger, leadName, leadType, leadStepNumber);
    }
    pageLoaded = false;
  }

  // Track form interaction (focus on any input)
  if (formImpressed) {
    formNode
      .querySelectorAll('input, textarea, select, button, [contenteditable="true"], [tabindex]')
      .forEach((input) => {
        input.addEventListener('focusin', () => {
          hasSucceded = false;
          if (!formStarted && !hasInteracted) {
            formStarted = true;
            hasInteracted = true;
            trackFormInteraction(formTrigger, leadName, leadType, leadStepNumber);
          }
        });
      });
  }

  // Store the last tracked values
  let lastTrackedSuccess: { leadId: string; leadEmail: string; values: object } | null = null;
  let lastTrackedError: { leadErrorType: string; leadErrorMessage: string } | null = null;

  // Track form success event
  formNode.addEventListener('formSuccess', (event: Event) => {
    const { leadEmail } = (event as CustomEvent).detail;
    const leadId = saltedHash(leadEmail);
    const hashedEmail = saltedHash(leadEmail);
    let marketing_consent = '';
    let profiling_consent = '';
    let hasMarketingConsentCheckbox = false;

    const targetForm = event.target as HTMLFormElement;

    if (targetForm?.elements) {
      Array.from(targetForm.elements).forEach((element) => {
        const input = element as HTMLInputElement;
        const inputName = input.name?.toLowerCase() || '';

        if (input.type === 'checkbox' && inputName.includes('marketingconsent')) {
          hasMarketingConsentCheckbox = true;
          marketing_consent = input.checked ? 'optin' : 'optout';
        }

        if (input.type === 'checkbox' && inputName.includes('profilingconsent')) {
          profiling_consent = input.checked ? 'optin' : 'optout';
        }
      });
    }

    const action = (
      targetForm?.querySelector<HTMLInputElement>('input[name="action"]')?.value ?? ''
    ).toLowerCase();
    const webformCode = targetForm?.querySelector<HTMLInputElement>(
      'input[name="webformCode"]'
    )?.value;
    const customWebformCode = targetForm?.querySelector<HTMLInputElement>(
      'input[name="customWebformCode_skip"]'
    )?.value;
    const effectiveWebformCode =
      webformCode?.toLowerCase() === 'customvalues' && customWebformCode
        ? customWebformCode
        : webformCode ?? '';
    const isNewsletterForm =
      action === 'subscriptions' || effectiveWebformCode.toLowerCase() === 'newslettersignup';

    if (!hasMarketingConsentCheckbox && isNewsletterForm) {
      marketing_consent = 'optin';
    }

    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const values: Record<string, string> = {};

    Array.from(form.elements).forEach((element) => {
      if ((element as HTMLInputElement).name) {
        values[(element as HTMLInputElement).name] = (element as HTMLInputElement).value;
      }
    });

    if (
      (!lastTrackedSuccess ||
        lastTrackedSuccess.leadId !== leadId ||
        lastTrackedSuccess.leadEmail !== hashedEmail ||
        lastTrackedSuccess.values !== values) &&
      !hasSucceded
    ) {
      lastTrackedSuccess = { leadId, leadEmail: hashedEmail, values }; // Update the last tracked success

      hasSucceded = true;
      trackFormSuccess(
        formTrigger,
        leadName,
        leadType,
        leadStepNumber,
        leadId,
        hashedEmail,
        marketing_consent,
        profiling_consent
      );
    }
  });

  // Track form error event
  formNode.addEventListener('formError', (event: Event) => {
    const errorElement = (event as CustomEvent).target as HTMLElement;
    const { lead_error_type, lead_error_message } = getFieldFromAPIError(errorElement);

    // Check if the values are different from the last tracked error
    if (lastTrackedError) {
      if (
        (lastTrackedError?.leadErrorType !== lead_error_type ||
          lastTrackedError?.leadErrorMessage !== lead_error_message) &&
        !hasFailed
      ) {
        lastTrackedError = { leadErrorType: lead_error_type, leadErrorMessage: lead_error_message }; // Update the last tracked error
        hasFailed = true;
        trackFormError(
          formTrigger,
          leadName,
          leadType,
          leadStepNumber,
          lead_error_type,
          lead_error_message
        );
      }
    }
  });
};
let isFormInteractionActive = false; // Tracks if a form interaction is ongoing
let lockedTriggerSource: string | null = null; // Stores the locked source for the current interaction

// Function to set the trigger source based on the clicked element
const setTriggerSourceBasedOnClick = (event: MouseEvent) => {
  if (isFormInteractionActive) {
    return; // Do not update trigger source if a form interaction is ongoing
  }

  const targetElement = event.target as HTMLElement;

  if (!targetElement) {
    setTriggerSource('displayed');
    mscSet('session', 'triggerSource', 'displayed');
    return;
  }

  const containsKeywordInAncestors = (element: HTMLElement, keyword: string): boolean => {
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
      if (
        Array.from(currentElement.classList).some((className) =>
          className.toLowerCase().includes(keyword)
        )
      ) {
        return true;
      }
      currentElement = currentElement.parentElement;
    }

    return false;
  };

  let source = 'displayed'; // Default value
  if (
    (targetElement.classList.contains('cmp-tabs__tab') &&
      (targetElement.closest('.modal') || targetElement.closest('.cmp-container__modal'))) ||
    (targetElement.closest('.cmp-container__modal') &&
      targetElement.classList.contains('closedbyx'))
  ) {
    source = 'displayed';
  } else if (containsKeywordInAncestors(targetElement, 'header')) {
    source = 'header menu';
  } else if (containsKeywordInAncestors(targetElement, 'footer')) {
    source = 'footer';
  } else if (targetElement.tagName === 'A') {
    source = 'internal link';
  }

  setTriggerSource(source);
  mscSet('session', 'triggerSource', source);
};

// Set up the click event listener to track trigger sources
document.addEventListener('click', (event: MouseEvent) => {
  const targetElement = event.target as HTMLElement;
  // Always set the trigger source for navigational elements
  if (
    (targetElement.tagName === 'A' && (targetElement as HTMLAnchorElement).href) ||
    targetElement.closest('a[href]') ||
    targetElement.classList.value.includes('black-overlay') ||
    (targetElement.classList.contains('closedbyx') &&
      targetElement.classList.value.includes('modal'))
  ) {
    setTriggerSourceBasedOnClick(event);
    mscRemove('session', 'lockedTriggerSource');
    lockedTriggerSource = null;
  }
});

// Retrieve the trigger source from sessionStorage when the page loads
const savedTriggerSource =
  mscGet<string>('session', 'lockedTriggerSource') || mscGet<string>('session', 'triggerSource');
if (savedTriggerSource) {
  setTriggerSource(savedTriggerSource);
}

// Track forms that have already been impressed
const impressedForms = new Set<HTMLElement>();
let userHasClicked = false; // Flag to track whether the user has clicked

// Listen for click events to set the flag
window.addEventListener('click', () => {
  userHasClicked = true; // Mark that the user has clicked
});

const handleFormVisibility = (entries: IntersectionObserverEntry[]) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const formNode = entry.target as HTMLElement;

      // If the form has already been impressed and the user hasn't clicked, skip the impression
      if (impressedForms.has(formNode) && !userHasClicked) {
        return;
      }

      // If it's the first impression, mark it as impressed
      if (!impressedForms.has(formNode)) {
        impressedForms.add(formNode);
      }

      // Trigger tracking (first impression or after a click)
      setupFormTracking(formNode);
      trackFormErrorsOnSubmit(formNode);
      // After handling the form, reset userHasClicked flag to false
      userHasClicked = false;
    }
    hasInteracted = false;
    hasFailed = false;
    hasSucceded = false;
  });
};

const getTriggerSource = (): string => {
  return mscGet<string>('session', 'triggerSource') || 'displayed';
};
const handleFormFocus = (event: FocusEvent) => {
  const form = event.target as HTMLFormElement;

  if (form && !lockedTriggerSource) {
    lockedTriggerSource = getTriggerSource(); // Lock the trigger source
    isFormInteractionActive = true;

    mscSet('session', 'lockedTriggerSource', lockedTriggerSource);
  }
};

// Form blur handler to reset the state
const handleFormBlur = (event: FocusEvent) => {
  const form = event.target as HTMLFormElement;

  if (form) {
    isFormInteractionActive = false;
  }
};

const handleFormSubmit = (event: SubmitEvent) => {
  const form = event.target as HTMLFormElement;

  if (form && lockedTriggerSource) {
    setTriggerSource(lockedTriggerSource);
    mscSet('session', 'triggerSource', lockedTriggerSource);
  }
};

// Add event listeners to all forms
document.querySelectorAll('form').forEach((form) => {
  form.addEventListener('focusin', handleFormFocus);
  form.addEventListener('focusout', handleFormBlur);
  form.addEventListener('submit', handleFormSubmit);
});

window.addEventListener('beforeunload', () => {
  // Reset the locked trigger source only when navigating away
  mscRemove('session', 'lockedTriggerSource');
  lockedTriggerSource = null;
});

// Intersection observer to track visibility of forms
const observer = new IntersectionObserver(handleFormVisibility, {
  root: null,
  threshold: 0.1,
});

// Observe all forms on the page
const observeForms = () => {
  document.querySelectorAll('.cmp-form').forEach((formNode) => {
    if (!impressedForms.has(<HTMLElement>formNode)) {
      observer.observe(formNode); // Start observing the form
    }
  });
};

// Function to get the field error type and message from the error element
function getFieldFromAPIError(errorElement: HTMLElement) {
  const leadErrorTypes: string[] = [];
  const leadErrorMessages: string[] = [];

  // Extract label for error type (if any)
  const labelElement = errorElement.querySelector('label');
  if (labelElement) {
    const labelText = labelElement.textContent?.trim().toLowerCase() ?? '';
    if (labelText) leadErrorTypes.push(labelText);
  }

  // Extract error messages from `.error-list-validation`
  const errorList = errorElement.querySelector('.error-list-validation');
  if (errorList) {
    const errorMessages = Array.from(errorList.children).map((child) => {
      if (child.classList.contains('input-hidden-error-messagge-vr')) return ''; // Skip hidden errors
      return child.textContent?.trim().toLowerCase() ?? '';
    });

    leadErrorMessages.push(...errorMessages.filter((msg) => msg));
  }

  return {
    lead_error_type: leadErrorTypes.join('|'),
    lead_error_message: leadErrorMessages.join('|'),
  };
}
const trackFormErrorsOnSubmit = (formNode: HTMLElement) => {
  // Store the last tracked error values
  let lastTrackedError: { leadErrorType: string; leadErrorMessage: string } | null = null;

  // Find the submit button in the form
  const submitButton = formNode.querySelector(
    '.cmp-form__buttons-container .cmp-button.cmp-button__submit'
  );

  if (!submitButton) {
    console.warn('Submit button not found in form');
    return;
  }

  // Attach click event listener to the submit button
  submitButton.addEventListener('click', (event) => {
    event.preventDefault(); // Prevent the default form submission for error tracking
    const errorTypes: string[] = [];
    const errorMessages: string[] = [];

    // Find all `.error-input` elements
    formNode.querySelectorAll('.error-input, .flatepickr-error-input').forEach((errorInput) => {
      // Find the closest label and extract its text, removing " *"
      const labelElement = errorInput.querySelector('label');
      if (labelElement) {
        const leadErrorType =
          labelElement.textContent?.replace(' *', '').trim().toLowerCase() ?? '';
        if (leadErrorType) errorTypes.push(leadErrorType);
      }

      // Look for `.error-list-validation` within the current error input
      const errorList = errorInput.querySelector('.error-list-validation');
      if (errorList) {
        const errorMessagesList = Array.from(errorList.children).map((child) => {
          // Skip hidden error messages
          if (
            Array.from(child.classList).some((className) =>
              className.includes('input-hidden-error-messagge-vr')
            )
          ) {
            return ''; // Skip hidden error messages
          }
          return child.textContent?.trim().toLowerCase() ?? '';
        });
        errorMessages.push(...errorMessagesList.filter((msg) => msg));
      }

      // Check for `.flatpickr-error`
      const errorDate = errorInput.querySelector('.flatpickr-error');
      if (errorDate && !errorDate.classList.contains('hidden')) {
        errorMessages.push(errorDate.textContent?.trim().toLowerCase() ?? '');
      }
    });
    // Select all checkbox containers with the class "error-select"
    const checkboxContainers = formNode.querySelectorAll('.error-select');

    checkboxContainers.forEach((checkboxContainer) => {
      // Find all checkboxes within this container
      const checkboxInputs = checkboxContainer.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-validation="vr-mandatory-field"]'
      );
      const errorField = checkboxContainer.querySelector('.vr-mandatory-field');
      // Check if there is more than one checkbox
      if (checkboxInputs.length > 1) {
        // Validate that at least one checkbox is checked
        let isAnyCheckboxChecked = false;

        checkboxInputs.forEach((checkbox) => {
          if (checkbox.checked) {
            isAnyCheckboxChecked = true;
          }
        });

        if (!isAnyCheckboxChecked) {
          if (errorField) {
            errorMessages.push(
              errorField.textContent?.trim().toLowerCase() || 'At least one option must be selected'
            );
          } else {
            errorMessages.push('At least one option must be selected');
          }
          errorTypes.push('checkbox');
        }
      } else {
        // Single checkbox case: validate it directly
        const singleCheckbox = checkboxInputs[0];
        if (singleCheckbox && !singleCheckbox.checked) {
          if (errorField) {
            errorMessages.push(
              errorField.textContent?.trim().toLowerCase() || 'The field is required'
            );
          } else {
            errorMessages.push('The field is required');
          }
          errorTypes.push('checkbox');
        }
      }
    });
    const dropdownContainers = formNode.querySelectorAll('.cmp-form-options--drop-down');
    dropdownContainers.forEach((dropdownContainer) => {
      const dropdownInput = dropdownContainer.querySelector<HTMLSelectElement>(
        'select[data-validation="vr-mandatory-field"]'
      );
      const labelElement = dropdownContainer.querySelector('label.cmp-form-options__label');

      if (dropdownInput?.value === '') {
        // Extract the error type

        if (labelElement) {
          const errorType = labelElement.textContent?.replace(' *', '').trim().toLowerCase() ?? '';
          if (errorType) {
            errorTypes.push(errorType);
            const errorMessage = 'The field is required';
            errorMessages.push(errorMessage);
          }
        }
      }
    });

    const uploaders = formNode.querySelectorAll('.cmp-file-uploader');
    uploaders.forEach((uploader) => {
      // Check for visible error messages
      const errorMessagesInUploader = uploader.querySelectorAll<HTMLParagraphElement>(
        '.cmp-file-uploader__error--generic, .cmp-file-uploader__error--type, .cmp-file-uploader__error--size'
      );
      const visibleErrorMessage = Array.from(errorMessagesInUploader).find(
        (error) => getComputedStyle(error).display !== 'none' && error.textContent?.trim()
      );

      // Check for the presence of the error-list with vr-mandatory-field
      const mandatoryFieldError = uploader.querySelector('.error-list .vr-mandatory-field');
      const isMandatoryFieldVisible =
        mandatoryFieldError && getComputedStyle(mandatoryFieldError).display !== 'none';

      // Populate errorType and errorMessage if conditions are met
      if (visibleErrorMessage || isMandatoryFieldVisible) {
        const errorMessage = visibleErrorMessage?.textContent?.trim() || 'The field is required';
        const errorType = 'uploading file';

        errorTypes.push(errorType);
        errorMessages.push(errorMessage);
      }
    });

    // Concatenate errors for tracking
    const uniqueErrorTypes = Array.from(new Set(errorTypes)); // Remove duplicates
    const concatenatedErrorType = uniqueErrorTypes.join('|');
    const concatenatedErrorMessage = errorMessages.join('|');

    // Check if there are actual errors to track (both error type and error messages should not be empty)
    if (concatenatedErrorType !== '' || concatenatedErrorMessage !== '') {
      // Check if the current errors differ from the last tracked errors
      if (
        !lastTrackedError ||
        lastTrackedError.leadErrorType !== concatenatedErrorType ||
        lastTrackedError.leadErrorMessage !== concatenatedErrorMessage ||
        !hasFailed
      ) {
        if (lastTrackedError)
          if (
            lastTrackedError.leadErrorType !== concatenatedErrorType ||
            lastTrackedError.leadErrorMessage !== concatenatedErrorMessage
          )
            hasFailed = false;

        failCheck = !hasFailed;
        // Update the last tracked error
        lastTrackedError = {
          leadErrorType: concatenatedErrorType,
          leadErrorMessage: concatenatedErrorMessage,
        };

        // Fetch dynamic form details
        const formName = formNode.querySelector<HTMLInputElement>('input[name="webformCode"]');
        const formId = formName?.closest('form')?.id;
        const webformCode = formName ? formName.value : '';
        let altWebformCode = webformCode;
        if (webformCode === 'customValues') {
          // is reading value about meta customWebformCode_skip
          altWebformCode =
            formName?.nextElementSibling instanceof HTMLInputElement
              ? formName.nextElementSibling.value
              : '';
        }
        let formTrigger = triggerInfo.source;
        if (
          formId &&
          mscGet<string>('session', formId) &&
          mscGet<string>('session', formId) != formTrigger
        )
          formTrigger = mscGet<string>('session', formId) ?? '';

        let leadName = leadNameMap[webformCode] ?? '';
        if (webformCode === 'customValues')
          leadName = altWebformCode ? leadNameMap[altWebformCode] : '';

        if (!isIE) {
          if (altWebformCode == 'cmbStickyBanner') {
            leadName = 'call me back - interested';
            if (
              mscGet<string>('session', 'triggerSource') != 'header menu' &&
              mscGet<string>('session', 'triggerSource') != 'footer'
            )
              formTrigger = 'sticky banner';
            else if (mscGet<string>('session', 'triggerSource') == 'header menu') {
              formTrigger = 'header menu';
            }
          }

          if (altWebformCode == 'cmbR4Q') {
            formTrigger = 'internal link';
          }
        }

        let leadType;
        if (!isIE) {
          if (altWebformCode == 'cmbStickyBanner') {
            if (
              mscGet<string>('session', 'triggerSource') != 'header menu' &&
              mscGet<string>('session', 'triggerSource') != 'footer'
            )
              leadType = 'modal';
            else if (mscGet<string>('session', 'triggerSource') == 'header menu')
              leadType = 'popup';
            else {
              leadType =
                formNode.closest('.modal') || formNode.closest('.cmp-container__modal')
                  ? 'pop up'
                  : 'form page';
            }
          } else {
            leadType =
              formNode.closest('.modal') || formNode.closest('.cmp-container__modal')
                ? 'pop up'
                : 'form page';
          }
        } else {
          leadType =
            formNode.closest('.modal') || formNode.closest('.cmp-container__modal')
              ? 'pop up'
              : 'form page';
        }
        const leadStepNumber = 'single-step'; // Currently, all forms are single-step

        if (!hasFailed && failCheck) {
          // Track the error
          hasFailed = true;
          trackFormError(
            formTrigger, // dynamic formTrigger
            leadName, // dynamic leadName
            leadType, // dynamic leadType
            leadStepNumber, // static 'single-step'
            concatenatedErrorType, // concatenated error types
            concatenatedErrorMessage // concatenated error messages
          );
        }
      }
    }
  });
};

const onDocumentReady = () => {
  // MutationObserver to dynamically observe newly added forms
  const mutationObserver = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.matches('.cmp-form')) {
            if (!impressedForms.has(node)) {
              observer.observe(node); // Start observing new form nodes
            }
          }
        });
      }
    });
  });

  // Start observing the forms initially
  observeForms();

  // Start observing dynamically added forms
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

// Set up a MutationObserver to detect DOM changes
const mutationObserver = new MutationObserver((mutationsList) => {
  for (const mutation of mutationsList) {
    if (mutation.type === 'childList') {
      observeForms(); // Re-run each time the DOM changes
    }
  }
});

// Start observing changes to the entire document body
const urlValue = window.location.pathname;
if (urlValue.includes('/search')) {
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState !== 'loading') {
  onDocumentReady();
} else {
  document.addEventListener('DOMContentLoaded', onDocumentReady);
}
